/**
 * Client-side pre-checks for uploads.
 *
 * The backend publishes its upload policy at `GET /api/v1/uploads/policy` and
 * enforces it again on every upload; this module reads that same policy so a
 * bad file is refused at pick time, with the same wording, before any bytes
 * leave the machine. Nothing here is hardcoded on purpose: the type lists come
 * from the endpoint, so a change on the server needs no client release.
 *
 * If the policy cannot be fetched every check passes and the server decides.
 */

import { config } from '@/config/env'

export type UploadContext = 'attachment' | 'image' | 'avatar'

export type UploadErrorCode =
  | 'UNSAFE_FILENAME'
  | 'BLOCKED_FILE_TYPE'
  | 'UNSUPPORTED_FILE_TYPE'
  | 'FILE_CONTENT_MISMATCH'
  | 'FILE_TOO_LARGE'

type SignaturePart = { at: number; hex: string } | { within: number; hex: string }

export interface UploadPolicy {
  version: number
  filenameMaxLength: number
  headBytes: number
  blockedExtensions: string[]
  blockedInnerExtensions: string[]
  mismatchTolerantFamilies: string[]
  contexts: Record<UploadContext, { maxBytes: number; extensions: string[] }>
  types: Record<string, { mime: string; family: string; signatures: SignaturePart[][] | null }>
}

export type UploadCheck = { ok: true } | { ok: false; code: UploadErrorCode; message: string }

const MESSAGES = {
  UNSAFE_FILENAME: 'This file name contains characters that are not allowed.',
  BLOCKED_FILE_TYPE: "This type of file can't be sent for security reasons.",
  UNSUPPORTED_FILE_TYPE: "This file type isn't supported.",
}

const mismatchMessage = (ext: string) => `This file's contents don't match its .${ext} extension.`
const tooLargeMessage = (maxBytes: number) =>
  `This file is larger than the ${Math.round(maxBytes / (1024 * 1024))} MB limit.`

const ERROR_CODES: ReadonlySet<string> = new Set([
  'UNSAFE_FILENAME',
  'BLOCKED_FILE_TYPE',
  'UNSUPPORTED_FILE_TYPE',
  'FILE_CONTENT_MISMATCH',
  'FILE_TOO_LARGE',
])

/* ===== Policy fetch ===== */

let cached: UploadPolicy | null = null
let inflight: Promise<UploadPolicy | null> | null = null
let failedAt = 0

const FETCH_TIMEOUT_MS = 3000
const RETRY_AFTER_FAILURE_MS = 60_000

/** Fetched once per page load. Resolves null on any failure (a timeout counts)
 *  and remembers it for a minute, so a dead endpoint costs one wait, not one
 *  per pick; after that the next call retries. */
export function getUploadPolicy(): Promise<UploadPolicy | null> {
  if (cached) return Promise.resolve(cached)
  if (inflight) return inflight
  if (failedAt && Date.now() - failedAt < RETRY_AFTER_FAILURE_MS) return Promise.resolve(null)
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  inflight = fetch(`${config.apiUrl}/api/v1/uploads/policy`, { signal: controller.signal })
    .then(async (res) => {
      if (!res.ok) return null
      const json = (await res.json()) as UploadPolicy
      return json?.contexts && json?.types ? json : null
    })
    .catch(() => null)
    .then((policy) => {
      clearTimeout(timer)
      cached = policy
      failedAt = policy ? 0 : Date.now()
      inflight = null
      return policy
    })
  return inflight
}

/** Test seam: drop the cached policy. */
export function resetUploadPolicyCache(): void {
  cached = null
  inflight = null
  failedAt = 0
}

/* ===== Checks ===== */

// U+0000-1F, U+007F-9F, zero-width and bidi controls, BOM.
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/

const TEXT_MARKUP_PREFIXES = ['<!doctype', '<html', '<script', '<svg', '<?xml', '<iframe']

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2)
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return out
}

function matchesAt(head: Uint8Array, offset: number, needle: Uint8Array): boolean {
  if (offset + needle.length > head.length) return false
  for (let i = 0; i < needle.length; i++) if (head[offset + i] !== needle[i]) return false
  return true
}

function matchesPart(head: Uint8Array, part: SignaturePart): boolean {
  const needle = hexToBytes(part.hex)
  if ('at' in part) return matchesAt(head, part.at, needle)
  const limit = Math.min(part.within, head.length) - needle.length
  for (let i = 0; i <= limit; i++) if (matchesAt(head, i, needle)) return true
  return false
}

function matchesSignatures(head: Uint8Array, signatures: SignaturePart[][] | null): boolean {
  if (!signatures) return false
  return signatures.some((alt) => alt.every((part) => matchesPart(head, part)))
}

/** The text rule, on the bytes we have: no binary, no markup a viewer could
 *  execute. UTF-16 is let through on its BOM. */
function looksLikeSafeText(head: Uint8Array): boolean {
  const utf16 = head.length >= 2 && ((head[0] === 0xff && head[1] === 0xfe) || (head[0] === 0xfe && head[1] === 0xff))
  if (utf16) return true
  if (head.includes(0)) return false
  let start = 0
  if (head[0] === 0xef && head[1] === 0xbb && head[2] === 0xbf) start = 3
  while (start < head.length && (head[start] === 0x20 || (head[start] >= 0x09 && head[start] <= 0x0d))) start++
  const lead = new TextDecoder('latin1').decode(head.subarray(start, start + 16)).toLowerCase()
  return !TEXT_MARKUP_PREFIXES.some((p) => lead.startsWith(p))
}

function fail(code: UploadErrorCode, message: string): UploadCheck {
  return { ok: false, code, message }
}

export async function checkFileForUpload(file: File, context: UploadContext): Promise<UploadCheck> {
  const policy = await getUploadPolicy()
  if (!policy) return { ok: true }
  const ctx = policy.contexts[context]
  if (!ctx) return { ok: true }

  // Rules 1-4: name.
  const raw = file.name || ''
  const base = raw.slice(Math.max(raw.lastIndexOf('/'), raw.lastIndexOf('\\')) + 1)
  if (UNSAFE_CHARS.test(base)) return fail('UNSAFE_FILENAME', MESSAGES.UNSAFE_FILENAME)
  // Windows drops trailing dots and spaces, so `a.exe.` is `a.exe`.
  const name = base.replace(/[. ]+$/, '')
  if (!name || name.length > policy.filenameMaxLength) {
    return fail('UNSAFE_FILENAME', MESSAGES.UNSAFE_FILENAME)
  }

  // Rules 5-7: extension.
  const segments = name.split('.').slice(1).map((s) => s.trim().toLowerCase())
  // A last segment of only digits is a date or version ("Invoice 22.10.2026"),
  // not an extension: the bytes decide, as for a name with no dot.
  const last = segments.length ? segments[segments.length - 1] : ''
  const ext = /^\d+$/.test(last) ? '' : last
  if (ext && policy.blockedExtensions.includes(ext)) return fail('BLOCKED_FILE_TYPE', MESSAGES.BLOCKED_FILE_TYPE)
  if (segments.slice(0, -1).some((s) => policy.blockedInnerExtensions.includes(s))) {
    return fail('BLOCKED_FILE_TYPE', MESSAGES.BLOCKED_FILE_TYPE)
  }
  if (ext && !ctx.extensions.includes(ext)) return fail('UNSUPPORTED_FILE_TYPE', MESSAGES.UNSUPPORTED_FILE_TYPE)

  // Content rule 1: size.
  if (file.size > ctx.maxBytes) return fail('FILE_TOO_LARGE', tooLargeMessage(ctx.maxBytes))
  if (file.size === 0) return fail('UNSUPPORTED_FILE_TYPE', MESSAGES.UNSUPPORTED_FILE_TYPE)

  let head: Uint8Array
  try {
    head = new Uint8Array(await file.slice(0, policy.headBytes).arrayBuffer())
  } catch {
    // Unreadable here (revoked handle, permissions): let the server decide.
    return { ok: true }
  }

  // Rule 8: no extension, so the bytes are the only evidence.
  if (!ext) {
    const sniffed = ctx.extensions.some((e) => matchesSignatures(head, policy.types[e]?.signatures ?? null))
    return sniffed ? { ok: true } : fail('UNSUPPORTED_FILE_TYPE', MESSAGES.UNSUPPORTED_FILE_TYPE)
  }

  const type = policy.types[ext]
  if (!type) return { ok: true }

  // Text types have no signature; the text rule is their whole check.
  if (type.signatures === null) {
    return looksLikeSafeText(head) ? { ok: true } : fail('FILE_CONTENT_MISMATCH', mismatchMessage(ext))
  }
  if (matchesSignatures(head, type.signatures)) return { ok: true }

  // A mislabelled photo or clip is common and harmless: accept it when it is
  // really another type of the same family.
  if (policy.mismatchTolerantFamilies.includes(type.family)) {
    const sibling = ctx.extensions.some((e) => {
      const t = policy.types[e]
      if (!t || t.family !== type.family) return false
      return t.signatures === null ? looksLikeSafeText(head) : matchesSignatures(head, t.signatures)
    })
    if (sibling) return { ok: true }
  }
  return fail('FILE_CONTENT_MISMATCH', mismatchMessage(ext))
}

/** For `<input accept>`. Empty until the policy has loaded, which the browser
 *  reads as "anything": the checks above still run on pick. */
export function acceptAttributeFor(context: UploadContext): string {
  const ctx = cached?.contexts[context]
  return ctx ? ctx.extensions.map((e) => `.${e}`).join(',') : ''
}

/** The server's own wording when it refused an upload for one of the policy
 *  reasons, else null so the caller keeps its generic message. */
export function uploadErrorMessage(responseJson: unknown): string | null {
  const error = (responseJson as { error?: { code?: unknown; message?: unknown } } | null)?.error
  const code = typeof error?.code === 'string' ? error.code : ''
  if (!ERROR_CODES.has(code)) return null
  if (typeof error?.message === 'string' && error.message) return error.message
  if (code === 'FILE_TOO_LARGE') return 'This file is larger than the allowed limit.'
  if (code === 'FILE_CONTENT_MISMATCH') return "This file's contents don't match its extension."
  return MESSAGES[code as keyof typeof MESSAGES]
}
